import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Phone, Check, ArrowRight, BadgeCheck } from "lucide-react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/motion";
import InsuranceProviders from "@/components/sections/InsuranceProviders";
import { CONTACT } from "@/data/content";
import useSEO from "@/hooks/useSEO";

const PLANS = [
  {
    name: "Sun Life Financial",
    color: "#F1B434",
    tag: "Extended Health Benefits",
    domain: "sunlife.ca",
    notes: "Most Sun Life extended-health plans include mental-health and substance-use services. We&apos;ll help you understand your annual maximum, deductible, and any pre-authorisation steps.",
    tips: ["Ask HR for your Booklet #", "Check 'residential treatment' rider", "Some plans require pre-authorisation"],
  },
  {
    name: "Manulife",
    color: "#00A758",
    tag: "Personal & Group Plans",
    domain: "manulife.ca",
    notes: "Manulife group and personal plans commonly cover a portion of inpatient addiction treatment. Coverage varies by plan and employer group.",
    tips: ["Group number & member ID needed", "Ask about 'mental health & addiction'", "Coverage is often subject to annual max"],
  },
  {
    name: "Canada Life",
    color: "#C8102E",
    tag: "Group & Individual",
    domain: "canadalife.com",
    notes: "Formerly Great-West Life. Coverage frequently includes residential addiction treatment when medically necessary. Deductibles and coinsurance apply.",
    tips: ["Look for 'Great-West Life' legacy plans", "Ask about co-insurance %", "Direct billing not always available"],
  },
  {
    name: "Desjardins",
    color: "#00874E",
    tag: "Group Insurance",
    domain: "desjardins.com",
    notes: "Desjardins group plans (especially Quebec-based employers) often include coverage for addiction treatment. Confirm eligibility and reimbursement rate.",
    tips: ["Contract number required", "Reimbursement is usually 80%", "Save all receipts and clinical notes"],
  },
  {
    name: "Blue Cross",
    color: "#003DA5",
    tag: "Regional Plans (Ontario, Alberta, Atlantic)",
    domain: "bluecross.ca",
    notes: "Blue Cross provincial plans may include residential treatment coverage depending on the specific policy. We work with Ontario, Alberta and Atlantic Blue Cross plans.",
    tips: ["Check your provincial plan", "Confirm 'residential treatment' rider", "Reimbursement varies by region"],
  },
  {
    name: "Empire Life",
    color: "#003366",
    tag: "Group Benefits",
    domain: "empire.ca",
    notes: "Empire Life often provides coverage for inpatient addiction and mental-health treatment through group plans.",
    tips: ["Plan-specific limits", "May require doctor's referral", "Reimbursement varies"],
  },
  {
    name: "Union / Trade Coverage",
    color: "#2E6A8A",
    tag: "Union Benefits (LiUNA, IBEW, Teamsters, etc.)",
    domain: null,
    notes: "Many Canadian unions and trades provide dedicated addiction-recovery benefits through their benefits trust or member-assistance program (MAP).",
    tips: ["Contact your union rep first", "Ask about Member Assistance Plan (MAP)", "Some cover 100% including travel"],
  },
  {
    name: "Cowan / Beneva / Others",
    color: "#5B4E96",
    tag: "Additional Providers",
    domain: null,
    notes: "We work with most Canadian benefits providers. If your plan isn&apos;t listed here, contact us — we&apos;ve likely worked with it.",
    tips: ["Bring your benefits booklet", "Or call us with plan info", "We handle the paperwork for you"],
  },
];

const PlanLogo = ({ p }) => {
  const [failed, setFailed] = useState(false);
  if (!p.domain || failed) {
    return (
      <span className="grid place-items-center w-12 h-12 rounded-full shrink-0" style={{ background: `${p.color}18`, color: p.color }}>
        <BadgeCheck className="w-6 h-6" />
      </span>
    );
  }
  return (
    <span className="grid place-items-center w-12 h-12 rounded-xl bg-white border border-line shrink-0 p-1.5">
      <img
        src={`https://icon.horse/icon/${p.domain}`}
        alt={`${p.name} logo`}
        className="w-full h-full object-contain"
        onError={() => setFailed(true)}
        loading="lazy"
        draggable={false}
      />
    </span>
  );
};

const OtherInsurancePage = () => {
  const navigate = useNavigate();
  useSEO({
    title: "Insurance Coverage for Rehab in Toronto — Sun Life, Manulife, Canada Life & More",
    description:
      "We work with most Canadian insurers for inpatient addiction treatment in Toronto: Sun Life, Manulife, Canada Life, Desjardins, Empire Life, Blue Cross and union/trade plans. Verify your benefits in 60 seconds.",
    keywords: "insurance coverage rehab Toronto, Sun Life addiction treatment, Manulife rehab coverage, Canada Life inpatient treatment, union benefits rehab Ontario, rehab insurance Canada",
    canonical: "https://newbeginningsrecovery.ca/other-insurance",
  });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div data-testid="other-insurance-page" className="bg-sand min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="pt-40 md:pt-48 pb-20 bg-forestdark text-sand relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06]" style={{
          backgroundImage: "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }} aria-hidden />
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 relative">
          <Reveal>
            <p className="text-[11px] uppercase tracking-[0.3em] text-terracotta font-semibold mb-4 flex items-center gap-2">
              <ShieldCheck className="w-3.5 h-3.5" /> Coverage guide
            </p>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              Other Insurance <span className="italic text-terracotta">Coverage</span>
            </h1>
            <p className="mt-6 max-w-2xl text-sand/80 text-lg leading-relaxed">
              Beyond GreenShield direct-billing, we work with most Canadian insurers and union benefit
              plans. Below is a quick-reference guide — but the fastest way to know for sure is our
              free, confidential benefits check.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button
                onClick={() => navigate("/verify-benefits")}
                data-testid="other-insurance-verify"
                className="h-12 px-6 rounded-full bg-terracotta hover:bg-[#a8664b] text-white font-semibold"
              >
                Verify My Benefits <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
              <a
                href={CONTACT.phoneHref}
                data-testid="other-insurance-call"
                className="h-12 px-6 rounded-full bg-white/10 border border-white/25 text-white inline-flex items-center gap-2 font-semibold hover:bg-white/20 transition-colors"
              >
                <Phone className="w-4 h-4" /> {CONTACT.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Provider cards */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-[1300px] px-5 md:px-10">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-[11px] uppercase tracking-[0.28em] text-forest font-semibold">Provider by provider</p>
            <h2 className="mt-3 font-serif text-3xl md:text-4xl text-forest tracking-tight">Coverage we regularly work with</h2>
            <p className="mt-3 text-ink2">These are the plans we see most often. Coverage always depends on your specific policy — we&apos;ll verify the details for you at no cost.</p>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            {PLANS.map((p, i) => (
              <Reveal key={p.name} delay={i * 0.05}>
                <div className="relative rounded-2xl bg-white border border-line p-6 md:p-7 hover:shadow-[0_20px_40px_-16px_rgba(46,106,138,0.28)] transition-shadow h-full">
                  <span className="absolute top-0 left-0 right-0 h-1 rounded-t-2xl" style={{ background: p.color }} aria-hidden />
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-serif text-2xl md:text-3xl font-bold tracking-tight" style={{ color: p.color }}>{p.name}</p>
                      <p className="mt-1 text-[11px] uppercase tracking-[0.22em] text-ink2">{p.tag}</p>
                    </div>
                    <PlanLogo p={p} />
                  </div>
                  <p className="mt-4 text-ink2 leading-relaxed text-sm md:text-base" dangerouslySetInnerHTML={{ __html: p.notes }} />
                  <ul className="mt-4 space-y-2">
                    {p.tips.map((t) => (
                      <li key={t} className="flex items-start gap-2 text-[13px] text-ink">
                        <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: p.color }} />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ / how it works */}
      <section className="py-16 md:py-24 bg-sand2">
        <div className="mx-auto max-w-[1200px] px-5 md:px-10">
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { t: "Confidential", d: "Your benefits check never touches your permanent record. It&apos;s private, no obligation, no impact." },
              { t: "We handle the paperwork", d: "Our admissions team calls your provider, confirms coverage, and shares plain-English results with you." },
              { t: "Same-day options", d: "If your plan covers residential treatment, we can often admit you the same day. Don&apos;t wait — waiting is what addiction wants." },
            ].map((c) => (
              <div key={c.t} className="rounded-2xl bg-white border border-line p-6">
                <h3 className="font-serif text-forest text-xl font-semibold">{c.t}</h3>
                <p className="mt-2 text-ink2 text-sm leading-relaxed">{c.d}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Button
              onClick={() => navigate("/verify-benefits")}
              data-testid="other-insurance-verify-bottom"
              className="h-14 px-8 rounded-full bg-terracotta hover:bg-[#a8664b] text-white text-base font-semibold"
            >
              Verify My Benefits in 60 seconds <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
            <p className="mt-3 text-ink2 text-sm">or call <a href={CONTACT.phoneHref} className="text-terracotta font-semibold underline underline-offset-4">{CONTACT.phone}</a> — we&apos;re here 24/7</p>
          </div>
        </div>
      </section>

      <InsuranceProviders compact />

      <Footer />
    </div>
  );
};

export default OtherInsurancePage;
